import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Search, CheckCircle2, XCircle, HelpCircle, Loader2, Plus } from "lucide-react";

const HEALTH_META = {
  healthy: { label: "Healthy", icon: CheckCircle2, cls: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10" },
  failing: { label: "Failing", icon: XCircle, cls: "text-red-600 border-red-500/40 bg-red-500/10" },
  no_evidence: { label: "No evidence", icon: HelpCircle, cls: "text-amber-600 border-amber-500/40 bg-amber-500/10" },
};

function AddControlModal({ open, onClose, onSaved, frameworks }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [framework, setFramework] = useState(frameworks[0]?.key || "");
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/controls", {
        title, owner: owner || null, guidance: guidance || null,
        frameworks: framework ? [framework] : [],
      });
      toast({ title: "Control added" });
      setTitle(""); setOwner(""); setGuidance("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add control", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-control-add-modal">
        <DialogHeader>
          <DialogTitle>New control</DialogTitle>
          <DialogDescription>Add a control manually and map it to a framework.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-control-add-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Owner</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-control-add-owner-input" />
            </div>
            <div>
              <Label>Framework</Label>
              <Select value={framework} onValueChange={setFramework}>
                <SelectTrigger className="mt-1" data-testid="compliance-control-add-framework-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {frameworks.map((f) => <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Guidance</Label>
            <Textarea value={guidance} onChange={(e) => setGuidance(e.target.value)} className="mt-1" rows={2} data-testid="compliance-control-add-guidance-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-control-add-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-control-add-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add control"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ControlDrawer({ control, onClose, isAdmin, onUpdated }) {
  const { toast } = useToast();
  const [owner, setOwner] = useState(control?.owner || "");
  const [status, setStatus] = useState(control?.status || "not_started");
  const [busy, setBusy] = useState(false);

  if (!control) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/compliance/controls/${control.id}`, { owner, status });
      toast({ title: "Control updated" });
      onUpdated?.(data);
    } catch (e) {
      toast({ title: "Couldn't update control", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const meta = HEALTH_META[control.health] || HEALTH_META.no_evidence;

  return (
    <Dialog open={!!control} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="compliance-control-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-[13px] text-muted-foreground">{control.code}</span> {control.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <span className={cn("inline-flex items-center gap-1 text-[11px] font-semibold uppercase rounded-full border px-2 py-0.5 w-fit", meta.cls)}>
            <meta.icon className="w-3.5 h-3.5" /> {meta.label}
          </span>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Guidance</div>
            <div className="text-[13px]">{control.guidance}</div>
          </div>
          <div className="text-[12.5px] text-muted-foreground">Mapped frameworks: {(control.frameworks || []).map((f) => f.toUpperCase()).join(", ")}</div>
          <div className="text-[12.5px] text-muted-foreground">Evidence linked: {control.evidence || 0}</div>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <label className="text-[11.5px] text-muted-foreground">Owner</label>
                <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-control-owner-input" />
              </div>
              <div>
                <label className="text-[11.5px] text-muted-foreground">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1" data-testid="compliance-control-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not started</SelectItem>
                    <SelectItem value="implemented">Implemented</SelectItem>
                    <SelectItem value="failing">Failing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="compliance-control-save-btn"
                className="col-span-2 inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceControls({ isAdmin, initialFrameworkFilter }) {
  const [controls, setControls] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fwFilter, setFwFilter] = useState(initialFrameworkFilter || "all");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ctRes, fwRes] = await Promise.all([api.get("/compliance/controls"), api.get("/compliance/frameworks")]);
      setControls(ctRes.data.controls || []);
      setFrameworks(fwRes.data.frameworks || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => controls.filter((c) => {
    if (fwFilter !== "all" && !c.frameworks?.includes(fwFilter)) return false;
    if (search && !`${c.code} ${c.title}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [controls, fwFilter, search]);

  if (loading) return <div className="h-72 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="compliance-controls-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search controls…" className="pl-8 h-9" data-testid="compliance-controls-search" />
        </div>
        <Select value={fwFilter} onValueChange={setFwFilter}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="compliance-controls-framework-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All frameworks</SelectItem>
            {frameworks.map((f) => <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => setAddOpen(true)} data-testid="compliance-control-add-btn">
            <Plus className="w-3.5 h-3.5" /> New control
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-controls-table">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Control</TableHead>
              <TableHead>Frameworks</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Health</TableHead>
              <TableHead className="text-right">Evidence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => {
              const meta = HEALTH_META[c.health] || HEALTH_META.no_evidence;
              return (
                <TableRow
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(c)}
                  data-testid={`compliance-control-row-${c.id}`}
                >
                  <TableCell className="font-mono text-[12px]">{c.code}</TableCell>
                  <TableCell className="text-[12.5px]">{c.title}</TableCell>
                  <TableCell className="text-[11.5px] text-muted-foreground">{(c.frameworks || []).map((f) => f.toUpperCase()).join(", ")}</TableCell>
                  <TableCell className="text-[12px]">{c.owner || "—"}</TableCell>
                  <TableCell>
                    <span className={cn("inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase rounded-full border px-2 py-0.5", meta.cls)}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-[12px]">{c.evidence || 0}</TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[12.5px] text-muted-foreground py-8">No controls match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ControlDrawer
        control={selected}
        isAdmin={isAdmin}
        onClose={() => setSelected(null)}
        onUpdated={(updated) => { setControls((prev) => prev.map((c) => (c.id === updated.id ? updated : c))); setSelected(null); }}
      />
      <AddControlModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} frameworks={frameworks} />
    </div>
  );
}
